/**
 * DuckDB-WASM service for local file analysis and fetched data storage
 *
 * Provides in-browser SQL analytics for:
 * - Local files (CSV, Parquet, JSON) uploaded by user
 * - Fetched data from remote databases (cached locally for exploration)
 */

import * as duckdb from '@duckdb/duckdb-wasm';

// Singleton instance
let db: duckdb.AsyncDuckDB | null = null;
let conn: duckdb.AsyncDuckDBConnection | null = null;
let initPromise: Promise<duckdb.AsyncDuckDB> | null = null;

// Track registered files (user uploads)
const registeredFiles = new Map<string, { name: string; size: number; type: string }>();

// Track fetched datasets (from remote queries)
const fetchedDatasets = new Map<string, DatasetMetadata>();

/**
 * Metadata for fetched datasets
 */
export interface DatasetMetadata {
  tableName: string;
  source: string;
  sourceQuery: string;
  question: string;
  rowCount: number;
  truncated: boolean;
  columns: ColumnMetadata[];
  description: string;
  fetchedAt: Date;
}

/**
 * Column metadata
 */
export interface ColumnMetadata {
  name: string;
  dataType: string;
  sampleValues: string[];
}

/**
 * Initialize DuckDB-WASM (singleton)
 */
export async function initDuckDB(): Promise<duckdb.AsyncDuckDB> {
  if (db) return db;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    const JSDELIVR_BUNDLES = duckdb.getJsDelivrBundles();
    const bundle = await duckdb.selectBundle(JSDELIVR_BUNDLES);

    const worker_url = URL.createObjectURL(
      new Blob([`importScripts("${bundle.mainWorker}");`], { type: 'text/javascript' })
    );

    const worker = new Worker(worker_url);
    const logger = new duckdb.ConsoleLogger(duckdb.LogLevel.WARNING);
    db = new duckdb.AsyncDuckDB(logger, worker);

    await db.instantiate(bundle.mainModule, bundle.pthreadWorker);
    URL.revokeObjectURL(worker_url);

    console.log('[DuckDB] Initialized successfully');
    return db;
  })();

  return initPromise;
}

/**
 * Get a connection to DuckDB
 */
export async function getConnection(): Promise<duckdb.AsyncDuckDBConnection> {
  if (conn) return conn;

  const database = await initDuckDB();
  conn = await database.connect();
  return conn;
}

/**
 * Register a file with DuckDB for querying
 */
export async function registerFile(file: File): Promise<string> {
  const database = await initDuckDB();

  // Use a sanitized table name based on filename
  const tableName = sanitizeTableName(file.name);

  // Read file into ArrayBuffer
  const buffer = await file.arrayBuffer();

  // Register the file with DuckDB
  await database.registerFileBuffer(file.name, new Uint8Array(buffer));

  // Create a table from the file
  const connection = await getConnection();
  const extension = file.name.split('.').pop()?.toLowerCase();

  let createTableSQL: string;

  switch (extension) {
    case 'csv':
      createTableSQL = `CREATE OR REPLACE TABLE "${tableName}" AS SELECT * FROM read_csv_auto('${file.name}')`;
      break;
    case 'parquet':
      createTableSQL = `CREATE OR REPLACE TABLE "${tableName}" AS SELECT * FROM read_parquet('${file.name}')`;
      break;
    case 'json':
      createTableSQL = `CREATE OR REPLACE TABLE "${tableName}" AS SELECT * FROM read_json_auto('${file.name}')`;
      break;
    default:
      throw new Error(`Unsupported file type: ${extension}`);
  }

  await connection.query(createTableSQL);

  // Track the registered file
  registeredFiles.set(tableName, {
    name: file.name,
    size: file.size,
    type: extension || 'unknown'
  });

  console.log(`[DuckDB] Registered file "${file.name}" as table "${tableName}"`);

  return tableName;
}

/**
 * Remove a registered file/table
 */
export async function unregisterFile(tableName: string): Promise<void> {
  const connection = await getConnection();
  await connection.query(`DROP TABLE IF EXISTS "${tableName}"`);
  registeredFiles.delete(tableName);
  console.log(`[DuckDB] Unregistered table "${tableName}"`);
}

/**
 * Convert BigInt values to numbers for JSON serialization
 */
function convertBigInts(obj: any): any {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj === 'bigint') return Number(obj);
  if (Array.isArray(obj)) return obj.map(convertBigInts);
  if (typeof obj === 'object') {
    const result: Record<string, any> = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = convertBigInts(value);
    }
    return result;
  }
  return obj;
}

/**
 * Execute a SQL query against local files
 */
export async function executeQuery(sql: string): Promise<QueryResult> {
  const connection = await getConnection();

  try {
    const result = await connection.query(sql);
    const rawRows = result.toArray().map((row: any) => Object.fromEntries(row));
    // Convert BigInt values to numbers for JSON serialization
    const rows = convertBigInts(rawRows);
    const columns = result.schema.fields.map((f: any) => ({
      name: f.name,
      type: f.type.toString()
    }));

    return {
      success: true,
      columns,
      rows,
      rowCount: rows.length
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
      columns: [],
      rows: [],
      rowCount: 0
    };
  }
}

/**
 * Get schema information for a table
 */
export async function getTableSchema(tableName: string): Promise<TableSchema> {
  const connection = await getConnection();

  const result = await connection.query(`DESCRIBE "${tableName}"`);
  const rows = result.toArray().map((row: any) => Object.fromEntries(row));

  return {
    tableName,
    columns: rows.map((row: any) => ({
      name: row.column_name,
      type: row.column_type,
      nullable: row.null === 'YES'
    }))
  };
}

/**
 * Get all registered local tables
 */
export function getRegisteredFiles(): LocalFile[] {
  return Array.from(registeredFiles.entries()).map(([tableName, info]) => ({
    tableName,
    fileName: info.name,
    size: info.size,
    type: info.type
  }));
}

/**
 * Get schema information for all local tables (for LLM context)
 */
export async function getAllTableSchemas(): Promise<TableSchema[]> {
  const files = getRegisteredFiles();
  const schemas: TableSchema[] = [];

  for (const file of files) {
    const schema = await getTableSchema(file.tableName);
    schemas.push(schema);
  }

  return schemas;
}

/**
 * Check if DuckDB is initialized
 */
export function isInitialized(): boolean {
  return db !== null;
}

/**
 * Check if there are any local files registered
 */
export function hasLocalFiles(): boolean {
  return registeredFiles.size > 0;
}

// =============================================================================
// Fetched Data Management (from remote queries)
// =============================================================================

/**
 * Store fetched data from a remote query into DuckDB
 */
export async function storeFetchedData(
  tableName: string,
  data: Record<string, any>[],
  metadata: Omit<DatasetMetadata, 'tableName' | 'fetchedAt'>
): Promise<void> {
  if (data.length === 0) {
    console.warn(`[DuckDB] No data to store for table "${tableName}"`);
    return;
  }

  const connection = await getConnection();

  // Drop existing table if it exists
  await connection.query(`DROP TABLE IF EXISTS "${tableName}"`);

  // Infer column types from first row
  const firstRow = data[0];
  const columns = Object.entries(firstRow).map(([name, value]) => {
    let type = 'VARCHAR';
    if (typeof value === 'number') {
      type = Number.isInteger(value) ? 'BIGINT' : 'DOUBLE';
    } else if (typeof value === 'boolean') {
      type = 'BOOLEAN';
    }
    return { name, type };
  });

  // Create table
  const columnDefs = columns.map(c => `"${c.name}" ${c.type}`).join(', ');
  await connection.query(`CREATE TABLE "${tableName}" (${columnDefs})`);

  // Insert data in batches
  const batchSize = 1000;
  for (let i = 0; i < data.length; i += batchSize) {
    const batch = data.slice(i, i + batchSize);
    const values = batch.map(row => {
      const vals = columns.map(c => {
        const val = row[c.name];
        if (val === null || val === undefined) return 'NULL';
        if (typeof val === 'string') return `'${val.replace(/'/g, "''")}'`;
        if (typeof val === 'boolean') return val ? 'TRUE' : 'FALSE';
        return String(val);
      });
      return `(${vals.join(', ')})`;
    }).join(', ');

    await connection.query(`INSERT INTO "${tableName}" VALUES ${values}`);
  }

  // Store metadata
  fetchedDatasets.set(tableName, {
    tableName,
    ...metadata,
    fetchedAt: new Date(),
  });

  console.log(`[DuckDB] Stored ${data.length} rows in table "${tableName}"`);
}

/**
 * Get all fetched datasets (metadata registry)
 */
export function getFetchedDatasets(): DatasetMetadata[] {
  return Array.from(fetchedDatasets.values());
}

/**
 * Get metadata for a specific dataset
 */
export function getDatasetMetadata(tableName: string): DatasetMetadata | undefined {
  return fetchedDatasets.get(tableName);
}

/**
 * Check if a dataset exists
 */
export function hasDataset(tableName: string): boolean {
  return fetchedDatasets.has(tableName);
}

/**
 * Remove a fetched dataset
 */
export async function removeFetchedDataset(tableName: string): Promise<void> {
  const connection = await getConnection();
  await connection.query(`DROP TABLE IF EXISTS "${tableName}"`);
  fetchedDatasets.delete(tableName);
  console.log(`[DuckDB] Removed fetched dataset "${tableName}"`);
}

/**
 * Clear all fetched datasets (session reset)
 */
export async function clearFetchedDatasets(): Promise<void> {
  const connection = await getConnection();
  const tableNames = Array.from(fetchedDatasets.keys());
  for (const tableName of tableNames) {
    await connection.query(`DROP TABLE IF EXISTS "${tableName}"`);
  }
  fetchedDatasets.clear();
  console.log('[DuckDB] Cleared all fetched datasets');
}

/**
 * Check if there are any fetched datasets
 */
export function hasFetchedDatasets(): boolean {
  return fetchedDatasets.size > 0;
}

// Helper function to sanitize file names for use as table names
function sanitizeTableName(fileName: string): string {
  // Remove extension and replace invalid characters
  const baseName = fileName.replace(/\.[^/.]+$/, '');
  return baseName
    .replace(/[^a-zA-Z0-9_]/g, '_')
    .replace(/^[0-9]/, '_$&')  // Prefix numbers with underscore
    .toLowerCase();
}

// Types
export interface QueryResult {
  success: boolean;
  columns: { name: string; type: string }[];
  rows: Record<string, any>[];
  rowCount: number;
  error?: string;
}

export interface TableSchema {
  tableName: string;
  columns: {
    name: string;
    type: string;
    nullable: boolean;
  }[];
}

export interface LocalFile {
  tableName: string;
  fileName: string;
  size: number;
  type: string;
}
